<template>
  <BaseTypeEditShell
    base-type="events"
    :title="data?.name || 'Veranstaltung bearbeiten'"
    :subtitle="data?.name ? 'Veranstaltung bearbeiten' : null"
    :in-review="isNeedsReview"
  >
    <template v-if="data">
      <Form
        class="base-type-edit__form"
        @submit="saveObject"
        v-slot="{ errors, values, setFieldValue, setFieldError }"
        :initial-values="data"
        :validation-schema="saveSchema"
        ref="formEvent"
      >
        <NeedsReviewGuard :in-review="isNeedsReview">
          <FreePackageHint
            v-if="hasFreePackageForEventsBaseType"
            class="mb-4 mb-lg-5"
            :route-query="$route.query"
          />

          <fieldset class="fieldset mb-4 mb-lg-5">
            <legend>Allgemein</legend>
            <div class="form-floating">
              <Field
                name="name"
                id="form-event-name"
                class="form-control"
                :class="{'is-invalid': errors.name}"
                autocomplete="off"
                placeholder="Titel der Veranstaltung"
              />
              <label class="form-label form-label--required" for="form-event-name">Titel der Veranstaltung<span class="visually-hidden"> (Pflichtfeld)</span></label>
              <ErrorMessage name="name" :class="errorMessageClasses"/>
            </div>
            <PremiumPhoneToggle
              input-id="form-event-show-tel-fax"
              :locked="!hasPaidOrIndividualPackageForEventsBaseType"
              :phone-number="organisationPhone"
              :route-query="$route.query"
            />
          </fieldset>

          <fieldset class="fieldset mb-4 mb-lg-5">
            <legend>Terminart</legend>
            <p class="base-type-edit__section-intro">
              Findet die Veranstaltung durchgehend über einen Zeitraum statt oder zu konkreten Einzelterminen?
            </p>
            <div class="row g-3">
              <div class="col-12 col-lg-6">
                <label class="event-schedule-choice" :class="{'event-schedule-choice--active': values.event_schedule_type === 'period'}">
                  <Field name="event_schedule_type" type="radio" value="period" class="form-check-input"/>
                  <span><strong>Zeitraum</strong><small>Durchgehend von Start- bis Enddatum</small></span>
                </label>
              </div>
              <div class="col-12 col-lg-6">
                <label class="event-schedule-choice" :class="{'event-schedule-choice--active': values.event_schedule_type === 'dates'}">
                  <Field name="event_schedule_type" type="radio" value="dates" class="form-check-input"/>
                  <span><strong>Einzeltermine</strong><small>Ein oder mehrere konkrete Termine</small></span>
                </label>
              </div>
            </div>
            <ErrorMessage name="event_schedule_type" :class="errorMessageClasses"/>
          </fieldset>

          <fieldset v-if="values.event_schedule_type === 'period'" class="fieldset mb-4 mb-lg-5">
            <legend>Zeitraum</legend>
            <div class="row g-3">
              <div class="col-12 col-lg-6">
                <label class="form-label form-label--required" for="form-event-start-on">Startdatum<span class="visually-hidden"> (Pflichtfeld)</span></label>
                <Field id="form-event-start-on" type="date" name="start_on" class="form-control" :class="{'is-invalid': errors.start_on}" aria-required="true"/>
                <ErrorMessage name="start_on" :class="errorMessageClasses"/>
              </div>
              <div class="col-12 col-lg-6">
                <label class="form-label form-label--required" for="form-event-end-on">Enddatum<span class="visually-hidden"> (Pflichtfeld)</span></label>
                <Field id="form-event-end-on" type="date" name="end_on" class="form-control" :class="{'is-invalid': errors.end_on}" aria-required="true"/>
                <ErrorMessage name="end_on" :class="errorMessageClasses"/>
              </div>
            </div>
          </fieldset>

          <fieldset v-if="values.event_schedule_type === 'dates'" class="fieldset mb-4 mb-lg-5">
            <legend>Einzeltermine</legend>
            <p class="mb-2">Jeder Termin kann einen eigenen Namen, Uhrzeiten, eine Preisangabe und einen Ticket-Link erhalten.</p>
            <ErrorMessage name="event_dates_attributes" :class="errorMessageClasses"/>
            <FieldArray name="event_dates_attributes" v-slot="{ fields, push, remove }">
              <div v-if="fields.length" class="event-date-list mb-3">
                <details v-for="(field, index) in fields" :key="field.key" class="event-date-item" :open="fields.length === 1 || index === newlyAddedEventDateIndex">
                  <summary>
                    <span class="event-date-item__number">{{ index + 1 }}</span>
                    <span class="event-date-item__summary">{{ formatEventDateSummary(field.value, index) }}</span>
                    <span class="event-date-item__edit">bearbeiten</span>
                  </summary>
                  <div class="event-date-item__body">
                    <Field :name="`event_dates_attributes[${index}].id`" type="hidden"/>
                    <div class="row g-3">
                      <div class="col-12 col-lg-6">
                        <label class="form-label" :for="`event-date-name-${index}`">Name</label>
                        <Field :id="`event-date-name-${index}`" :name="`event_dates_attributes[${index}].name`" class="form-control" placeholder="z.B. Eröffnung, Abendvorstellung"/>
                      </div>
                      <div class="col-12 col-lg-6">
                        <label class="form-label form-label--required" :for="`event-date-start-date-${index}`">Datum<span class="visually-hidden"> (Pflichtfeld)</span></label>
                        <Field :id="`event-date-start-date-${index}`" :name="`event_dates_attributes[${index}].start_date`" type="date" class="form-control" :class="{'is-invalid': errors[`event_dates_attributes[${index}].start_date`]}" aria-required="true"/>
                        <ErrorMessage :name="`event_dates_attributes[${index}].start_date`" :class="errorMessageClasses"/>
                      </div>
                      <div class="col-12 col-md-6 col-lg-3">
                        <label class="form-label" :for="`event-date-start-time-${index}`">Beginn</label>
                        <Field :id="`event-date-start-time-${index}`" :name="`event_dates_attributes[${index}].start_time`" type="time" class="form-control" :class="{'is-invalid': errors[`event_dates_attributes[${index}].start_time`]}"/>
                        <ErrorMessage :name="`event_dates_attributes[${index}].start_time`" :class="errorMessageClasses"/>
                      </div>
                      <div class="col-12 col-md-6 col-lg-3">
                        <label class="form-label" :for="`event-date-end-time-${index}`">Ende</label>
                        <Field :id="`event-date-end-time-${index}`" :name="`event_dates_attributes[${index}].end_time`" type="time" class="form-control" :class="{'is-invalid': errors[`event_dates_attributes[${index}].end_time`]}"/>
                        <ErrorMessage :name="`event_dates_attributes[${index}].end_time`" :class="errorMessageClasses"/>
                      </div>
                      <div class="col-12 col-lg-6">
                        <label class="form-label" :for="`event-date-price-${index}`">Preisangabe</label>
                        <Field :id="`event-date-price-${index}`" :name="`event_dates_attributes[${index}].price_indication`" type="number" min="0" step="0.01" class="form-control" :class="{'is-invalid': errors[`event_dates_attributes[${index}].price_indication`]}" placeholder="Ab-Preis in EUR"/>
                        <ErrorMessage :name="`event_dates_attributes[${index}].price_indication`" :class="errorMessageClasses"/>
                      </div>
                      <div class="col-12">
                        <label class="form-label" :for="`event-date-ticket-url-${index}`">
                          Ticket-URL
                          <span v-if="hasFreePackageForEventsBaseType" class="badge text-bg-warning ms-2">Premium</span>
                        </label>
                        <Field :id="`event-date-ticket-url-${index}`" :name="`event_dates_attributes[${index}].ticket_url`" type="text" class="form-control" :class="{'is-invalid': errors[`event_dates_attributes[${index}].ticket_url`]}" placeholder="www.example.com" @blur="setFieldValue(`event_dates_attributes[${index}].ticket_url`, normalizeHttpUrl($event.target.value))"/>
                        <div class="form-text">Ohne Protokoll ergänzen wir automatisch https://.</div>
                        <ErrorMessage :name="`event_dates_attributes[${index}].ticket_url`" :class="errorMessageClasses"/>
                        <PremiumFeatureHint
                          class="mt-2"
                          :visible="hasFreePackageForEventsBaseType"
                          :route-query="$route.query"
                          hint-text="Die Ticket-URL ist ein Premium-Feature. Sie können sie speichern, aber ohne Premium-Paket wird sie nicht auf den Portalen ausgeliefert."
                        />
                      </div>
                      <div class="col-12 text-end">
                        <button type="button" class="btn btn-outline-danger btn-sm" @click="removeEventDate(index, field.value, remove)">Termin entfernen</button>
                      </div>
                    </div>
                  </div>
                </details>
              </div>
              <p v-else class="event-date-empty">Noch kein Einzeltermin erfasst.</p>

              <div class="d-flex flex-wrap gap-2 mb-4">
                <button type="button" class="btn btn-outline-primary" :disabled="hasFreePackageForEventsBaseType && fields.length >= 1" @click="addEventDate(fields, push)">
                  {{ fields.length ? 'Weiteren Termin hinzufügen' : 'Ersten Termin hinzufügen' }}
                </button>
                <span v-if="hasFreePackageForEventsBaseType && fields.length >= 1" class="badge text-bg-warning align-self-center">Ab dem zweiten Termin: Premium</span>
              </div>

              <div class="position-relative">
                <div v-if="hasFreePackageForEventsBaseType" class="premium-banderole premium-banderole--flush-top"><span class="premium-banderole__label">premium</span></div>
                <div class="event-date-tools">
                  <h3 class="h5">Schnell-Aktionen</h3>
                  <div class="row g-2 align-items-end">
                    <div class="col-12 col-md-4">
                      <label class="form-label" for="event-repeat-interval">Letzten Termin wiederholen</label>
                      <select id="event-repeat-interval" v-model="repeatInterval" class="form-select" :disabled="hasFreePackageForEventsBaseType || !fields.length">
                        <option value="day">täglich</option>
                        <option value="week">wöchentlich</option>
                        <option value="month">monatlich</option>
                      </select>
                    </div>
                    <div class="col-7 col-md-3">
                      <label class="form-label" for="event-repeat-count">für</label>
                      <div class="input-group">
                        <input id="event-repeat-count" v-model.number="repeatCount" type="number" min="1" max="365" class="form-control" :disabled="hasFreePackageForEventsBaseType || !fields.length"/>
                        <span class="input-group-text">{{ repeatUnitLabel }}</span>
                      </div>
                    </div>
                    <div class="col-5 col-md-2">
                      <button type="button" class="btn btn-primary w-100" :disabled="hasFreePackageForEventsBaseType || !fields.length" @click="repeatEventDates(fields, push)">Anlegen</button>
                    </div>
                    <div class="col-12 col-md-3 d-grid gap-2">
                      <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="hasFreePackageForEventsBaseType || !fields.length" @click="removePastEventDates(fields, remove)">Vergangene entfernen</button>
                      <button type="button" class="btn btn-outline-danger btn-sm" :disabled="hasFreePackageForEventsBaseType || !fields.length" @click="removeAllEventDates(fields, remove)">Alle entfernen</button>
                    </div>
                  </div>
                  <PremiumFeatureHint
                    class="mt-3"
                    :visible="hasFreePackageForEventsBaseType"
                    :route-query="$route.query"
                    hint-text="Schnell-Aktionen und weitere Einzeltermine sind Premium-Features. Der erste Einzeltermin ist im Free-Paket enthalten."
                  />
                </div>
              </div>
            </FieldArray>
          </fieldset>

            <HostLocationSelector
              ref="hostLocationSelector"
              base-type="termine"
              :errors="errors"
              :values="values"
              :initial-values="data"
              :host-objects="hostObjects"
              :set-field-value="setFieldValue"
              :set-field-error="setFieldError"
              @location-change="handleHostLocationChange"
            />

            <fieldset class="fieldset fieldset--categories-by-group mb-4 mb-lg-5">
              <legend>Vermarktungskategorien</legend>
              <ErrorMessage name="categories_data" class="mb-3" :class="errorMessageClasses"/>
              <FieldArray name="categories_data" v-slot="{ fields }">
                <div class="base-type-edit__category-groups">
                  <div
                    v-for="(groupCategories, groupName) in groupedCategoryFields(fields)"
                    :key="groupName"
                    class="base-type-edit__category-group"
                  >
                    <h3 class="base-type-edit__category-group-title">{{ groupName }}</h3>
                    <div class="category-list">
                      <div
                        v-for="category in groupCategories"
                        :key="category.key"
                        class="category-item"
                      >
                        <Field
                          :name="`categories_data[${category.key}].id`"
                          type="hidden"
                          :value="category.value.id"
                        />

                        <Field
                          type="checkbox"
                          :name="`categories_data[${category.key}].checked`"
                          v-slot="{ field }"
                          :value="true"
                          :unchecked-value="false"
                        >
                          <input
                            type="checkbox"
                            v-bind="field"
                            class="form-check-input"
                            :id="`cb-category-${category.key}`"
                          />
                        </Field>
                        <label :for="`cb-category-${category.key}`">{{ categoryLabel(category.value) }}</label>
                      </div>
                    </div>
                  </div>
                </div>
              </FieldArray>
              <ErrorMessage name="categories_data" class="mb-3" :class="errorMessageClasses"/>
            </fieldset>

            <fieldset class="fieldset mb-4 mb-lg-5">
              <legend>Texte</legend>

              <div class="mb-3">
                <label class="form-label" for="form-event-introtext">Einleitungstext</label>
                <Field name="introtext" v-slot="{ field, handleChange }">
                  <TextEditor :height="180" :is-invalid="!!errors.introtext" v-model="field.value" @editorChange="handleChange"/>
                </Field>
                <ErrorMessage name="introtext" :class="errorMessageClasses"/>
              </div>

              <div class="mb-3">
                <label class="form-label form-label--required" for="form-event-content">Beschreibung<span class="visually-hidden"> (Pflichtfeld)</span></label>
                <Field name="content" v-slot="{ field, handleChange }">
                  <TextEditor :is-invalid="!!errors.content" v-model="field.value" @editorChange="handleChange"/>
                </Field>
                <ErrorMessage name="content" :class="errorMessageClasses"/>
              </div>
            </fieldset>

            <fieldset class="fieldset fieldset--media mb-4 mb-lg-5">
              <legend>Bilder zuweisen</legend>
              <p class="base-type-edit__media-intro">
                <Icon icon="fa-pro-solid:circle-info" />
                Wählen Sie die Bilder für diese Veranstaltung aus. Das erste Bild wird als Titelbild verwendet.
              </p>
              <Field
                v-slot="{ handleChange }"
                name="asset_ids"
                type="text"
                :value="tmpAssets.map(asset => asset.id)"
              >
                <AssetSelect
                  :validation-error="!!errors.asset_ids"
                  :lock-type-select="true"
                  :base-type="packageBaseType"
                  :assets="tmpAssets"
                  @assetSelect="onAssetSelectChange($event, handleChange)"
                />
              </Field>
              <ErrorMessage name="asset_ids" class="mb-3" :class="errorMessageClasses"/>
            </fieldset>

            <Field v-if="data.id" name="contact_form" v-slot="{ value, handleChange }">
              <ContactFormConfiguration
                :model-value="value"
                :booking-mode="values.inquiry_booking_mode"
                :booking-product-config-id="values.inquiry_booking_product_config_id"
                :external-booking-url="values.external_booking_url"
                :external-booking-system="values.external_booking_system"
                @update:model-value="handleChange"
                @update:booking-mode="setFieldValue('inquiry_booking_mode', $event)"
                @update:booking-product-config-id="setFieldValue('inquiry_booking_product_config_id', $event)"
                @update:external-booking-url="setFieldValue('external_booking_url', $event)"
              />
            </Field>

            <div class="position-relative">
              <div v-if="hasFreePackageForEventsBaseType" class="premium-banderole">
                <span class="premium-banderole__label">premium</span>
              </div>
            <fieldset class="fieldset mb-4 mb-lg-5">
              <legend>Externer Link</legend>
              <p class="text-muted small mb-3">
                Der externe Website-Link steht in Premium-Paketen zur Verfügung.
              </p>
              <div class="row">
                <div class="col-12 col-lg-6 mb-3">
                  <div class="form-floating">
                    <Field
                      name="external_website_url"
                      id="form-event-external-website-url"
                      class="form-control"
                      :class="{'is-invalid': errors.external_website_url}"
                      type="text"
                      placeholder="www.example.com"
                      @blur="setFieldValue('external_website_url', normalizeHttpUrl($event.target.value))"
                      aria-describedby="form-event-external-website-url-hint"
                    />
                    <label class="form-label" for="form-event-external-website-url">Externe Website URL</label>
                    <ErrorMessage name="external_website_url" :class="errorMessageClasses"/>
                  </div>
                  <div id="form-event-external-website-url-hint" class="form-text">Ohne Protokoll ergänzen wir automatisch https://.</div>
                </div>
              </div>
              <PremiumFeatureHint
                :visible="hasFreePackageForEventsBaseType"
                :route-query="$route.query"
              />
            </fieldset>
            </div>

          <BaseTypeFormToolbar
            :errors="errors"
            :object-name="values.name"
            :package-base-type="packageBaseType"
            :enabled="publicationState.enabled"
            :marketed="publicationState.setting_enable_in_manet_stadt_abl"
            :has-website="showPortalToggle"
            :busy="publicationBusy"
            :publication-errors="publicationErrors"
            :publication-message="publicationMessage"
            @publication-action="runPublicationAction($event, values)"
            :admin2-object-id="data?.id"
            :admin2-lang="currentLang"
            :preview-url="previewUrl"
            :show-premium-preview="hasFreePackageForEventsBaseType"
          >
            <template #controls>
              <ObjectNeedsReviewIcon
                v-if="isNeedsReview"
                class="base-type-edit__toolbar-review"
                :label="reviewLabel"
              />
            </template>
          </BaseTypeFormToolbar>
        </NeedsReviewGuard>
      </Form>
    </template>
  </BaseTypeEditShell>
</template>

<script>
import {useUserStore} from "@/stores/user";
import {Form, Field, ErrorMessage, FieldArray} from "vee-validate";
import Yup from '@/yup'
import {useCategoryStore} from "@/stores/category";
import objectPublication from '@/mixins/objectPublication';
import AssetSelect from "@/components/general/AssetSelect.vue";
import {useMediaStore} from "@/stores/media";
import TextEditor from "@/components/TextEditor.vue";
import {Helpers} from "@/helpers";
import {useLocalizedObjectStore} from "@/stores/localized_objects";
import ObjectNeedsReviewIcon from "@/components/objects/ObjectNeedsReviewIcon.vue";
import {OBJECT_REVIEW_LABEL} from "@/utils/objectReview";
import BaseTypeEditShell from "@/components/base_type/BaseTypeEditShell.vue";
import BaseTypeFormToolbar from "@/components/base_type/BaseTypeFormToolbar.vue";
import FreePackageHint from "@/components/base_type/FreePackageHint.vue";
import PremiumPhoneToggle from "@/components/base_type/PremiumPhoneToggle.vue";
import PremiumFeatureHint from "@/components/base_type/PremiumFeatureHint.vue";
import HostLocationSelector from "@/components/objects/HostLocationSelector.vue";
import ContactFormConfiguration from "@/components/objects/ContactFormConfiguration.vue";
import {
  hasFreePackageForBaseType,
  hasPaidOrIndividualPackageForBaseType,
  MAX_OBJECT_IMAGES,
  MAX_OBJECT_NAME_LENGTH,
} from '@/utils/objectFormPolicy';
import {normalizeHttpUrl} from '@/utils/httpUrl';
import NeedsReviewGuard from "@/components/base_type/NeedsReviewGuard.vue";
import {buildObjectPreviewUrl} from "@/utils/objectPreview";
import {useObjectStore} from "@/stores/object";
import dayjs from "dayjs";
import axios from "axios";
import {
  buildPhotonSearchUrl,
  filterPhotonHouseResults,
  mapPhotonFeatureToAddress,
  shouldSearchPhoton,
} from "@/scripts/locationSearch";
export default {
  mixins: [objectPublication],
  components: {
    AssetSelect,
    BaseTypeEditShell,
    BaseTypeFormToolbar,
    ObjectNeedsReviewIcon,
    ErrorMessage,
    Field,
    FieldArray,
    Form,
    TextEditor,
    FreePackageHint,
    PremiumPhoneToggle,
    PremiumFeatureHint,
    HostLocationSelector,
    ContactFormConfiguration,
    NeedsReviewGuard,
  },
  setup() {
    return {
      localizedObjectStore: useLocalizedObjectStore(),
      objectStore: useObjectStore(),
      userStore: useUserStore(),
      categoryStore: useCategoryStore(),
      mediaStore: useMediaStore(),
    }
  },
  data() {
    return {
      schema: Yup.object({
        name: Yup.string().max(MAX_OBJECT_NAME_LENGTH).required().label('Titel der Veranstaltung'),
        event_schedule_type: Yup.string().oneOf(['period', 'dates']).required(),
        start_on: Yup.string().nullable().when('event_schedule_type', {
          is: 'period',
          then: schema => schema.required('Bitte ein Startdatum eingeben.'),
        }),
        end_on: Yup.string().nullable().when('event_schedule_type', {
          is: 'period',
          then: schema => schema
            .required('Bitte ein Enddatum eingeben.')
            .test('after-start', 'Das Enddatum muss am oder nach dem Startdatum liegen.', function (value) {
              return !value || !this.parent.start_on || value >= this.parent.start_on
            }),
        }),
        location_id: Yup.number()
          .nullable()
          .test(
            'location-or-address',
            'Bitte Veranstaltungsort auswählen oder Adresse auswählen.',
            function (value) {
              return Boolean(this.parent.configured_host_object_id || value || String(this.parent.search || '').trim())
            }
          ),
        configured_host_object_id: Yup.number()
          .transform((value, originalValue) => originalValue === '' ? null : value)
          .nullable(),
        host_object_setting: Yup.boolean().nullable(),
        introtext: Yup.string().nullable().max(300).label('Einleitungstext'),
        content: Yup.string().required().label('Beschreibung'),
        external_website_url: Yup.string().httpUrl().nullable().label('Externe Website URL'),
        external_booking_url: Yup.string().httpUrl().nullable().label('Link zum Anfrageformular / Buchungssystem')
          .when('inquiry_booking_mode', {
            is: 'external_url',
            then: schema => schema.required(),
          }),
        inquiry_booking_product_config_id: Yup.number().nullable(),
        asset_ids: Yup.array()
          .min(1, 'Es wird mindestens ein Bild benötigt.')
          .max(MAX_OBJECT_IMAGES, `Es dürfen maximal ${MAX_OBJECT_IMAGES} Bilder ausgewählt werden.`)
          .required('Es wird mindestens ein Bild benötigt.'),
        event_dates_attributes: Yup.array()
          .of(Yup.object({
            id: Yup.number().nullable(),
            name: Yup.string().nullable(),
            start_date: Yup.string().required('Bitte ein Datum eingeben.').label('Datum'),
            start_time: Yup.string().nullable(),
            end_time: Yup.string()
              .nullable()
              .test('after-start-time', 'Das Ende muss nach dem Beginn liegen.', function (value) {
                const startTime = this.parent.start_time
                return !value || !startTime || value > startTime
              }),
            price_indication: Yup.string()
              .nullable()
              .test('valid-price', 'Bitte eine gültige Preisangabe eingeben.', (value) => {
                if (value == null || value === '') return true
                return Number.isFinite(Number(value))
              })
              .test('positive-price', 'Die Preisangabe darf nicht negativ sein.', (value) => {
                if (value == null || value === '') return true
                return Number(value) >= 0
              }),
            ticket_url: Yup.string().httpUrl().nullable().label('Ticket-URL'),
          }))
          .when('event_schedule_type', {
            is: 'dates',
            then: schema => schema.min(1, 'Bitte mindestens einen Einzeltermin eingeben.'),
          }),
        categories_data: Yup.array()
          .of(
            Yup.object({
              id: Yup.number().required(),
              checked: Yup.boolean(),
            })
          )
          .required("Bitte mindestens eine Kategorie auswählen")
          .test(
            "checked-items-validation",
            "Bitte mindestens eine und maximal 3 Kategorien auswählen",
            (arr) => {
              if (!arr) return false
              const checkedCount = arr.filter((item) => item.checked).length
              return checkedCount >= 1 && checkedCount <= 3
            }
          ),
      }),
      categories: [],
      hostObjects: [],
      errorMessageClasses: 'mt-1 d-block invalid-feedback',
      data: null,
      editAddressClient: false,
      address: {
        id: null,
        name: null,
        street: null,
        zip: null,
        town: null,
        country: null,
        latitude: null,
        longitude: null,
        parent_id: null,
      },
      tmpAssets: [],
      removedEventDateIds: [],
      newlyAddedEventDateIndex: null,
      repeatInterval: 'week',
      repeatCount: 1,
    }
  },
  async created() {
    const [{data}, categoryResponse, hostResponse] = await Promise.all([
      this.localizedObjectStore.apiShow(this.$route.params.id, this.currentLang),
      this.categoryStore.index({ base_type: 'termine' }),
      this.objectStore.apiHostCandidates(this.currentLang, this.$route.params.id),
    ])

    if (!data) {
      return
    }

    this.categories = Array.isArray(categoryResponse.data)
      ? categoryResponse.data
      : categoryResponse.data?.categories || []
    this.hostObjects = Array.isArray(hostResponse.data?.host_objects)
      ? hostResponse.data.host_objects
      : []
    this.applyLoadedEventData(data)

    this.userStore.fetchMyClients()
  },
  mounted() {
    this.mediaStore.filters.type = ['images']
  },
  computed: {
    publicationObject() {
      return this.data
    },
    publicationForm() {
      return this.$refs.formEvent
    },
    reviewLabel() {
      return OBJECT_REVIEW_LABEL
    },
    breadcrumbItems() {
      return [
        {}
      ]
    },
    objects() {
      return this.userStore.user.getObjects('accommodations')
    },
    locationId() {
      return this.data?.location_id ?? null
    },
    hasSubmittableLocation() {
      const loc = this.data?.location
      return Boolean(this.locationId || (loc?.street && String(loc?.name || '').trim()))
    },
    standortModel() {
      const loc = this.data?.location
      if (!loc || typeof loc !== 'object') {
        return null
      }
      const parts = [loc.street, [loc.zip, loc.town].filter(Boolean).join(' ')].filter(Boolean).join(', ')
      return {
        id: loc.id,
        name: loc.name,
        street: loc.street,
        zip: loc.zip,
        town: loc.town,
        country: loc.country,
        latitude: loc.latitude ?? loc.lat,
        longitude: loc.longitude ?? loc.lon,
        address_string: [loc.name, parts].filter(Boolean).join(' — '),
      }
    },
    user() {
      return this.userStore.user
    },
    packageBaseType() {
      return 'termine'
    },
    currentLang() {
      return this.$route.params.lang || this.data?.lang || 'de'
    },
    showPortalToggle() {
      return false
    },
    hasFreePackageForEventsBaseType() {
      return hasFreePackageForBaseType(
        this.userStore.user?.organisation?.active_packages,
        this.packageBaseType,
      )
    },
    hasPaidOrIndividualPackageForEventsBaseType() {
      return hasPaidOrIndividualPackageForBaseType(
        this.userStore.user?.organisation?.active_packages,
        this.packageBaseType,
      )
    },
    organisationPhone() {
      return this.userStore.user?.organisation?.phone || ''
    },
    isNeedsReview() {
      return !!this.data?.needs_review
    },
    previewUrl() {
      return buildObjectPreviewUrl(this.data, this.currentLang)
    },
    repeatUnitLabel() {
      return {
        day: this.repeatCount === 1 ? 'Tag' : 'Tage',
        week: this.repeatCount === 1 ? 'Woche' : 'Wochen',
        month: this.repeatCount === 1 ? 'Monat' : 'Monate',
      }[this.repeatInterval]
    },
  },
  methods: {
    normalizeHttpUrl,
    applyLoadedEventData(data) {
      const eventDates = Array.isArray(data.event_dates_attributes)
        ? data.event_dates_attributes
        : []
      const normalizedData = {
        ...data,
        event_schedule_type: data.event_schedule_type || (eventDates.length ? 'dates' : 'period'),
        location_id: data.location_id ?? null,
        configured_host_object_id: data.configured_host_object_id ?? null,
        host_object_setting: false,
        asset_ids: Array.isArray(data.assets) ? data.assets.map((asset) => asset.id) : [],
        event_dates_attributes: eventDates,
        categories_data: this.normalizeCategoriesData(data),
        show_tel_fax: !!data.show_tel_fax,
        setting_enable_in_manet_stadt_abl: !!data.enabled,
      }

      this.removedEventDateIds = []
      this.newlyAddedEventDateIndex = null
      this.data = normalizedData
      this.tmpAssets = [...(data.assets || [])]

      if (this.$refs.formEvent?.resetForm) {
        this.$refs.formEvent.resetForm({
          values: normalizedData,
        })
      }
    },
    handleHostLocationChange({id, location}) {
      this.data = {
        ...this.data,
        location_id: id,
        location,
      }
    },
    createEmptyEventDate() {
      return {
        name: '',
        start_date: '',
        start_time: '',
        end_time: '',
        price_indication: '',
        ticket_url: '',
      }
    },
    addEventDate(fields, push) {
      this.newlyAddedEventDateIndex = fields.length
      push(this.createEmptyEventDate())
    },
    removeEventDate(index, eventDate, remove) {
      if (!eventDate?.id) {
        remove(index)
        return
      }

      if (!this.removedEventDateIds.includes(eventDate.id)) {
        this.removedEventDateIds.push(eventDate.id)
      }

      remove(index)
    },
    formatEventDateSummary(eventDate, index) {
      if (!eventDate?.start_date) {
        return `Termin ${index + 1}: Datum noch nicht eingetragen`
      }

      const date = dayjs(eventDate.start_date).format('DD.MM.YYYY')
      const time = eventDate.start_time ? `, ${eventDate.start_time} Uhr` : ''
      const name = eventDate.name ? ` · ${eventDate.name}` : ''
      return `${date}${time}${name}`
    },
    repeatEventDates(fields, push) {
      const source = fields[fields.length - 1]?.value
      const count = Math.max(1, Math.min(Number(this.repeatCount) || 1, 365))
      if (!source?.start_date) {
        Helpers.emitter.emit('error', 'Bitte beim letzten Termin zuerst ein Datum eingeben.')
        return
      }

      for (let step = 1; step <= count; step += 1) {
        push({
          ...source,
          id: undefined,
          start_date: dayjs(source.start_date).add(step, this.repeatInterval).format('YYYY-MM-DD'),
        })
      }
    },
    removePastEventDates(fields, remove) {
      const today = dayjs().format('YYYY-MM-DD')
      for (let index = fields.length - 1; index >= 0; index -= 1) {
        if (fields[index].value?.start_date && fields[index].value.start_date < today) {
          this.removeEventDate(index, fields[index].value, remove)
        }
      }
    },
    removeAllEventDates(fields, remove) {
      for (let index = fields.length - 1; index >= 0; index -= 1) {
        this.removeEventDate(index, fields[index].value, remove)
      }
    },
    handleAddressChange(newAddress, setFieldValue, setFieldError) {
      const previousLocationId = this.data.location_id ? Number(this.data.location_id) : null
      this.address = { ...newAddress }

      if (!newAddress?.id && !newAddress?.street) {
        this.data.location_id = null
        this.data.location = null
        setFieldValue('location_id', null, true)
        return
      }

      const lid = newAddress.id ? Number(newAddress.id) : null
      this.data.location_id = lid
      this.data.location = this.normalizeLocationFromAddress(newAddress, lid)
      setFieldValue('location_id', lid, true)

      if (this.hasSubmittableLocation) {
        setFieldError('location_id', undefined)
      }

      if (lid && lid !== previousLocationId) {
        this.userStore.fetchMyClients(true)
      }
    },
    normalizeLocationFromAddress(address, id = address?.id ?? null) {
      return {
        id,
        name: address.name,
        street: address.street,
        zip: address.zip ?? address.postcode,
        town: address.town ?? address.city,
        country: address.country,
        latitude: address.latitude ?? address.lat,
        longitude: address.longitude ?? address.lon,
      }
    },
    createAddressPayloadFromLocation(location) {
      return {
        name: location.name,
        street: location.street,
        postcode: location.zip,
        city: location.town,
        country: location.country,
        lat: location.latitude,
        lon: location.longitude,
      }
    },
    async resolveLocationFromSearch(values) {
      const query = String(values.search || '').trim()
      if (!shouldSearchPhoton(query)) {
        return null
      }

      const {data} = await axios.get(buildPhotonSearchUrl(query))
      const [feature] = filterPhotonHouseResults(data.features)
      if (!feature) {
        return null
      }

      const address = mapPhotonFeatureToAddress(feature)
      return this.normalizeLocationFromAddress({
        ...address,
        name: values.address_name || values.name || address.street,
      })
    },
    async ensureLocationForSubmit(values) {
      const submittedLocationName = String(values.address_name || '').trim()
      const existingLocationName = String(this.data?.location?.name || '').trim()
      const shouldKeepExistingLocation = values.location_id
        && (!submittedLocationName || submittedLocationName === existingLocationName)

      if (shouldKeepExistingLocation) {
        return Number(values.location_id)
      }

      const location = this.data?.location
        ? {
          ...this.data.location,
          name: submittedLocationName || this.data.location.name,
        }
        : await this.resolveLocationFromSearch(values)
      if (!location?.street || !String(location?.name || '').trim()) {
        return null
      }

      const created = await this.userStore.createLocationFromAddress(this.createAddressPayloadFromLocation(location))
      if (!created?.id) {
        return null
      }

      const lid = Number(created.id)
      this.data.location_id = lid
      this.data.location = this.normalizeLocationFromAddress(created, lid)
      values.location_id = lid
      this.userStore.fetchMyClients(true)
      return lid
    },
    getSelectedCategoryIds(data) {
      if (Array.isArray(data.categories_data)) {
        return data.categories_data.filter((item) => item.checked).map((item) => item.id)
      }

      if (Array.isArray(data.categories)) {
        return data.categories.map((category) => category.id)
      }

      return []
    },
    normalizeCategoriesData(data) {
      if (Array.isArray(data.categories_data) && data.categories_data.length) {
        return data.categories_data.map((item) => this.enrichCategoryField(item))
      }

      const selectedIds = new Set(this.getSelectedCategoryIds(data).map((id) => Number(id)))

      return this.categories.map((category) => ({
        ...category,
        checked: selectedIds.has(Number(category.id)),
      }))
    },
    enrichCategoryField(item) {
      const catalogCategory = this.categories.find((category) => Number(category.id) === Number(item.id))

      return {
        ...catalogCategory,
        ...item,
        group_name: item.group_name || catalogCategory?.group_name || null,
      }
    },
    categoryLabel(category) {
      return category?.name || category?.label || this.categoryName(category?.id) || `#${category?.id}`
    },
    categoryName(id) {
      return this.categories.find((category) => category.id === id)?.name
    },
    groupedCategoryFields(fields) {
      return fields.reduce((groups, field) => {
        const groupName = field.value.group_name || 'Weitere Kategorien'
        groups[groupName] = groups[groupName] || []
        groups[groupName].push(field)
        return groups
      }, {})
    },
    async persistObject(data) {
      const currentEventDates = Array.isArray(data.event_dates_attributes)
        ? data.event_dates_attributes
        : []
      const removedIds = new Set(this.removedEventDateIds)

      if (data.event_schedule_type === 'period') {
        const loadedEventDates = Array.isArray(this.data?.event_dates_attributes)
          ? this.data.event_dates_attributes
          : []
        loadedEventDates.forEach((eventDate) => {
          if (eventDate.id) removedIds.add(eventDate.id)
        })
      }

      const payload = {
        ...data,
        asset_ids: this.tmpAssets.map((asset) => asset.id),
        event_dates_attributes: [
          ...(data.event_schedule_type === 'dates' ? currentEventDates : []),
          ...Array.from(removedIds).map((id) => ({
            id,
            _destroy: '1',
          })),
        ],
      }
      if (data.event_schedule_type === 'dates') {
        payload.start_on = null
        payload.end_on = null
      }
      if (!this.showPortalToggle) {
        payload.setting_enable_in_manet_stadt_abl = !!payload.enabled
      }
      delete payload.description
      delete payload.event_schedule_type

      const {data: updatedData} = await this.localizedObjectStore.apiUpdate(payload)
      this.applyLoadedEventData({
        ...data,
        ...updatedData,
        assets: updatedData?.assets ?? this.tmpAssets,
        event_schedule_type: data.event_schedule_type,
      })
    },
    async persistPublicationState(state) {
      await this.localizedObjectStore.apiUpdate({id: this.data.id, lang: this.currentLang, ...state})
      this.data = {...this.data, ...state}
    },
    onAssetSelectChange(selectedAssets, handleChange) {
      this.tmpAssets = selectedAssets
      handleChange(this.tmpAssets.map(asset => asset.id))
    },
  },
}
</script>

<style scoped>
.event-schedule-choice {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  height: 100%;
  padding: 1rem;
  border: 2px solid #d9d9e2;
  background: #fff;
  cursor: pointer;
}

.event-schedule-choice--active {
  border-color: #664caf;
  background: #f7f5fd;
}

.event-schedule-choice strong,
.event-schedule-choice small {
  display: block;
}

.event-date-list {
  overflow: hidden;
  border: 1px solid #dedbe9;
}

.event-date-item + .event-date-item {
  border-top: 1px solid #dedbe9;
}

.event-date-item summary {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 0.85rem 1rem;
  background: #fff;
  cursor: pointer;
  list-style: none;
}

.event-date-item summary::-webkit-details-marker {
  display: none;
}

.event-date-item[open] summary {
  background: #f7f5fd;
}

.event-date-item__number {
  display: grid;
  flex: 0 0 2rem;
  width: 2rem;
  height: 2rem;
  place-items: center;
  background: #664caf;
  color: #fff;
  font-weight: 700;
}

.event-date-item__summary {
  flex: 1;
  font-weight: 600;
}

.event-date-item__edit {
  color: #664caf;
  font-size: 0.875rem;
}

.event-date-item__body {
  padding: 1rem;
  border-top: 1px solid #dedbe9;
  background: #fff;
}

.event-date-empty {
  padding: 1rem;
  border: 1px dashed #aaa3bf;
  color: #676276;
  text-align: center;
}

.event-date-tools {
  padding: 1rem;
  border: 1px solid #dedbe9;
  background: #f8f7fb;
}
</style>
